import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { downloadBlob } from 'src/app/shared/utils/download.util';
import { Component, Inject, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { StatisticsEnum, TableStatisticsEnum } from "src/app/enums/assessmentStatus.enum";
import { RiskAssessment, RiskRegister, RiskLevel, RiskRegisterAgentEntry, LikelihoodColorLevel, ControlsSummaryPayload, RiskRegisterControl } from "src/app/models/risk-assessment.model";
import { RiskRegisterService } from "src/app/services/risk-register.service";
import { ErrorConstants } from "src/app/shared/constants/error.constants";
import { ToastService } from "src/app/services/toast.service";
import { UserService } from "src/app/services/user.service";
import { getEnumList } from "src/app/_helpers/enum-helper";
import { TranslateService } from "src/app/services/translate.service";
import { UnitSymbols } from "src/app/enums/units.enum";
import { ValueConversionService } from "src/app/services/value-conversion.service";
import { RiskRegisterReportEntity } from "src/app/services/template-mappers/entities/risk-register-template-mapper";
import { ENTITY_TYPES } from "src/app/shared/widget-registry";
import { buildRegisterStatisticValues, getRegisterStatisticValue } from "src/app/shared/utils/risk-statistic-value.util";
import { ReportGenerationService } from "src/app/services/report-generation.service";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { RiskRegisterMatrixColumn } from "src/app/models/risk-register-seg-page.model";
import { PdfGeneratedData } from "src/app/models/pdfGeneratedData.model";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { firstValueFrom } from "rxjs";
import nameOf from "src/app/shared/form/nameOf.helper";
import { RiskRegisterDisplayRowKind } from "src/app/enums/riskRegisterDisplayRowKind.enum";
import { RiskRegisterDisplayRow, RiskRegisterGroupHeaderRow } from "src/app/models/risk-register-grouping.model";
import { selectGroupedEntries } from "src/app/components/risk-register/risk-register-grouping.util";

interface IRiskRegistersReport {
  dataList: RiskRegister[];
  groupedRows?: RiskRegisterDisplayRow[];
  agents: Pick<RiskRegisterMatrixColumn, "agentId" | "agentName" | "consequenceRating">[];
  riskLevels: RiskLevel[];
  riskAssessment: RiskAssessment;
  qualitativeExceedanceLabel?: string;
  qualitativeFrequencyLabel?: string;
  exposureLikelihoodLevels?: LikelihoodColorLevel[];
  preselectedAgentIds?: string[];
  keepRowOrder?: boolean;
}

interface SegmentItem {
  segName: string;
  segId: string;
  selected: boolean;
}

interface AgentItem {
  agentName: string;
  agentId: string;
  consequenceRating: number | null;
  selected: boolean;
}

interface StatisticItem {
  key: string;
  selected: boolean;
  value: string;
  tableKey: string;
}

type PreviewRow = RiskRegister | RiskRegisterGroupHeaderRow;

type AllSelectedKey = 'segAllSelected' | 'agentAllSelected' | 'statisticAllSelected';

@Component({
  standalone: false,
  selector: "app-risk-registers-report-popup",
  templateUrl: "./risk-registers-report-popup.component.html",
  styleUrl: "./risk-registers-report-popup.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class RiskRegistersReportPopupComponent implements OnInit {
  segsList: SegmentItem[] = [];
  agentsList: AgentItem[] = [];
  statisticList: StatisticItem[] = [];
  segAllSelected = false;
  agentAllSelected = false;
  statisticAllSelected = false;
  riskLevels: RiskLevel[] = [];
  // Pre-computed legend labels, one per riskLevel (avoids a method call per cell in the template).
  riskLevelLabels: string[] = [];
  riskAssessment: RiskAssessment;
  dataList: RiskRegister[] = [];
  dataSource: PreviewRow[] = [];
  readonly groupHeaderColumns = ['groupHeader'];
  extraColumns: AgentItem[] = [];
  isTableLoading = false;
  fixedColumns: string[] = [
    nameOf<RiskRegister>(x => x.segCode),
    nameOf<RiskRegister>(x => x.segName),
    nameOf<RiskRegister>(x => x.numberOfWorkers),
    nameOf<RiskRegister>(x => x.numberOfContractors),
  ];
  displayedColumns = [...this.fixedColumns];
  noDataError = ErrorConstants.errorHtml;
  currentDate = new Date();
  logoUrl: string;
  qualitativeExceedanceLabel: string;
  qualitativeFrequencyLabel: string;
  exposureLikelihoodLevels: LikelihoodColorLevel[] = [];
  readonly previewRowLimit = RiskRegistersReportPopupComponent.PREVIEW_ROW_LIMIT;
  isPreviewCapped = false;
  previewSegCount = 0;

  private static readonly PREVIEW_ROW_LIMIT = 50;

  showReportGeneration = false;
  isGeneratingReport = false;
  reportEntity: RiskRegisterReportEntity | null = null;
  readonly entityType = ENTITY_TYPES.RISK_REGISTER;

  constructor(
    public dialogRef: MatDialogRef<RiskRegistersReportPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IRiskRegistersReport,
    private userService: UserService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private valueConversionService: ValueConversionService,
    private riskRegisterService: RiskRegisterService,
    private reportGenerationService: ReportGenerationService,
    private organizationLevelService: OrganizationLevelService
  ) {
    this.qualitativeExceedanceLabel = data.qualitativeExceedanceLabel || this.translateService.getDynamicTranslation('Exceedance Probability');
    this.qualitativeFrequencyLabel = data.qualitativeFrequencyLabel || this.translateService.getDynamicTranslation('Frequency of Exposure');
    this.exposureLikelihoodLevels = data.exposureLikelihoodLevels ?? [];
    this.setDataList();
    const segs = data.dataList.map(val => ({ segName: val.segName, segId: val.segId, selected: false }));
    this.segsList = this.keepsRowOrder ? segs : segs.sort((a, b) => (a.segName ?? '').localeCompare(b.segName ?? ''));
    const preselected = new Set(data.preselectedAgentIds ?? []);
    this.agentsList = data.agents.map(agent => ({
      agentName: agent.agentName,
      agentId: agent.agentId,
      consequenceRating: agent.consequenceRating,
      selected: preselected.has(agent.agentId)
    }));
    this.agentAllSelected = this.agentsList.length > 0 && this.agentsList.every(agent => agent.selected);
    this.statisticList = getEnumList(StatisticsEnum).map(val => ({
      key: val,
      selected: false,
      value: this.getStatisticDisplayValue(val),
      tableKey: TableStatisticsEnum[val as keyof typeof TableStatisticsEnum]
    }));

    this.riskLevels = data.riskLevels;
    this.riskLevelLabels = this.riskLevels.map((level, index) => this.buildRiskLevelLegendName(level, index));
    this.riskAssessment = data.riskAssessment;
    if (preselected.size) {
      this.updateTableData();
    }
  }

  private get notAvailable(): string {
    return this.translateService.getDynamicTranslation('N/A');
  }

  private getStatisticDisplayValue(statKey: string): string {
    if (statKey === 'likelihood') {
      return `${this.translateService.getDynamicTranslation('Likelihood')} (${this.qualitativeExceedanceLabel} × ${this.qualitativeFrequencyLabel})`;
    }

    return StatisticsEnum[statKey as keyof typeof StatisticsEnum];
  }

  ngOnInit(): void {
    this.userService.getTenantLogoUrl()
      .subscribe({
        next: logoUrl => {
          if (logoUrl) {
            this.logoUrl = logoUrl;
          }
        },
        error: () => {
          this.logoUrl = '';
        }
      });
  }

  setDataList() {
    const convertedNumber = (value: number | null, unit: number | null) => {
      if (value == null) return null;
      return unit == null
        ? parseFloat((value).toFixed(2))
        : parseFloat(this.valueConversionService.getParameterValueAsPerAgentUnit(value, unit).toFixed(2));
    };
    const convertedValue = (value: number | null, unit: number | null) => {
      const num = convertedNumber(value, unit);
      if (num == null) return '-';
      return num.toFixed(2) + ' ' + (unit != null ? UnitSymbols[unit] : '-');
    };
    this.dataList = this.data.dataList.map(reg => ({
      ...reg,
      registers: reg.registers.filter((r: RiskRegisterAgentEntry | undefined) => !!r).map((r: RiskRegisterAgentEntry) => ({
        ...r,
        quantitativeGeometricMean: convertedValue(r.quantitativeGeometricMean ?? null, r.agentUnits ?? null),
        quantitativeDecisionStatisticValue: convertedValue(r.quantitativeDecisionStatisticValue ?? null, r.agentUnits ?? null),
      })),
    }));
  }

  private withPreviewStatisticValues(rows: PreviewRow[]): PreviewRow[] {
    const notAvailable = this.notAvailable;
    return rows.map(row => this.isGroupHeader(row) ? row : {
      ...row,
      registers: row.registers?.map((register: RiskRegisterAgentEntry) => ({
        ...register,
        statisticValues: buildRegisterStatisticValues(register, this.exposureLikelihoodLevels, notAvailable),
      })),
    });
  }

  selectionChange(list: (SegmentItem | AgentItem | StatisticItem)[], select: boolean, isAllSelect = false, allKey: AllSelectedKey | '' = '', updateTable = true) {
    if (isAllSelect) {
      list.forEach(item => item.selected = select);
    } else if (allKey) {
      this[allKey] = list.every(item => item.selected === true);
    }
    if (updateTable) { this.updateTableData(); }
  }

  updateTableData() {
    this.isTableLoading = true;
    const selectedSegs = this.segsList.filter(seg => seg.selected).map(seg => seg.segId);

    const selectedIds = new Set(selectedSegs);
    const selectedRows = this.data.groupedRows
      ? this.buildGroupedRows(selectedIds)
      : this.dataList.filter(data => selectedIds.has(data.segId));
    const orderedRows = this.keepsRowOrder
      ? selectedRows
      : selectedRows.sort((a, b) => ((a as RiskRegister).segName ?? '').localeCompare((b as RiskRegister).segName ?? ''));
    this.isPreviewCapped = orderedRows.length > RiskRegistersReportPopupComponent.PREVIEW_ROW_LIMIT;
    this.dataSource = this.withPreviewStatisticValues(orderedRows.slice(0, RiskRegistersReportPopupComponent.PREVIEW_ROW_LIMIT));
    this.previewSegCount = this.dataSource.filter(row => !this.isGroupHeader(row)).length;
    this.extraColumns = this.agentsList.filter(agent => agent.selected);
    this.displayedColumns = [...this.fixedColumns];
    this.extraColumns.forEach(column => this.displayedColumns.push(column.agentName));
    this.isTableLoading = false;
  }

  private get keepsRowOrder(): boolean {
    return !!this.data.keepRowOrder || !!this.data.groupedRows;
  }

  isGroupHeaderRow = (_index: number, row: PreviewRow): boolean => this.isGroupHeader(row);

  private isGroupHeader(row: PreviewRow): row is RiskRegisterGroupHeaderRow {
    return 'kind' in row && row.kind === RiskRegisterDisplayRowKind.GroupHeader;
  }

  private buildGroupedRows(segIds: ReadonlySet<string>): PreviewRow[] {
    const selected = new Map(this.dataList.filter(register => segIds.has(register.segId)).map(register => [register.segId, register]));
    return selectGroupedEntries(this.data.groupedRows ?? [], selected).flatMap(({ header, row, source, startsGroup }): PreviewRow[] => [
      ...(startsGroup ? [header] : []),
      { ...source, numberOfWorkers: row.numberOfWorkers, numberOfContractors: row.numberOfContractors }
    ]);
  }

  cancel() {
    if (this.showReportGeneration) {
      this.showReportGeneration = false;
      this.reportEntity = null;
      return;
    }
    this.dialogRef.close();
  }

  /**
   * DASH-2751: Handles the generator's `(pdfGenerated)` output for the Risk Assessment surface.
   * Without this binding the generator's save promise (report-generation-popup: onGenerateReportForNewTab)
   * never settles — nobody invokes onComplete/onError — so the dialog hangs on "Generating report…" forever.
   * Mirrors the working sample-details handler: save the report inside try/catch, surfacing errors via the toast.
   * The service itself calls event.onComplete/onError, which settles the awaited promise and clears the spinner.
   */
  async onPdfGenerated(event: PdfGeneratedData): Promise<void> {
    const organizationName = await this.resolveOrganizationName(this.riskAssessment.organizationLevelId);

    try {
      await this.reportGenerationService.generateAndSaveReport(event, {
        entityIds: [event.entityId],
        entityType: event.entityType,
        organizationLevelId: this.riskAssessment.organizationLevelId,
        organizationLevelName: organizationName,
        metadataKey: RolePermissionObjects.RiskRegister
      }, event.openInNewTab ?? true);
    } catch {
      // Fail closed with a generic, translated message — never surface raw backend detail
      // (stack fragments, internal field names, S3 URLs) to the UI. Mirrors the peer save
      // handler in ReportGenerationService.generateAndSaveReport.
      this.toastService.displayErrorMessage('Failed to save report. Please try again', true);
    }
  }

  /** Resolves the org-level display name for the report record; org name is best-effort (empty on failure). */
  private async resolveOrganizationName(organizationLevelId: string): Promise<string> {
    try {
      const organizations = await firstValueFrom(this.organizationLevelService.getAll());
      return organizations.find(org => org.id === organizationLevelId)?.name || '';
    } catch {
      return '';
    }
  }

  generateReport() {
    const selectedSegs = this.segsList.filter(s => s.selected);
    const selectedAgents = this.agentsList.filter(a => a.selected);
    const selectedStats = this.statisticList.filter(s => s.selected);

    if (!selectedSegs.length || !selectedAgents.length) {
      this.toastService.showInfo(this.translateService.getDynamicTranslation('Select at least one SEG and one Agent'), false);
      return;
    }

    if (this.isGeneratingReport) { return; }
    this.isGeneratingReport = true;

    // DASH-2701: SEG-level controls are not embedded on the register dataList (unlike job-roles/tasks),
    // so the SEG Profile "Controls" widget renders blank without this. Fetch the per-SEG control summary
    // (groupBySeg=true → each row carries SegId/AgentId; no link-type filter → both RiskAssessment and
    // RiskTreatment controls) and merge by SegId before building the report entity. The endpoint reuses
    // the same org-scoped Query() and RA-id validation as the existing risk-treatment-report consumer,
    // so it is bounded to the RA the user already owns — no new tenant/IDOR surface.
    const payload: ControlsSummaryPayload = {
      riskAssessmentIds: [this.riskAssessment.id],
      groupBySeg: true
    };
    this.riskRegisterService.getControlsSummary(payload).subscribe({
      next: controls => {
        this.mergeControlsIntoDataList(controls);
        this.buildAndShowReport(selectedSegs, selectedAgents, selectedStats);
      },
      error: error => {
        // Controls are best-effort: still generate the report (the Controls section shows "No Data").
        this.toastService.showError(error);
        this.buildAndShowReport(selectedSegs, selectedAgents, selectedStats);
      }
    });
  }

  /** Groups the per-SEG control summary by SegId (de-duping a control repeated across agents in a SEG). */
  private mergeControlsIntoDataList(controls: RiskRegisterControl[]): void {
    const bySeg = new Map<string, Map<string, RiskRegisterControl>>();
    (controls ?? []).forEach(control => {
      if (control.segId == null) { return; }
      const key = `${control.controlCode}|${control.linkType}`;
      const segControls = bySeg.get(control.segId) ?? new Map<string, RiskRegisterControl>();
      if (!segControls.has(key)) { segControls.set(key, control); }
      bySeg.set(control.segId, segControls);
    });
    this.dataList = this.dataList.map(reg => ({
      ...reg,
      controls: Array.from(bySeg.get(reg.segId)?.values() ?? [])
    }));
  }

  private buildAndShowReport(selectedSegs: SegmentItem[], selectedAgents: AgentItem[], selectedStats: StatisticItem[]): void {
    this.reportEntity = {
      dataList: this.dataList,
      groupedRows: this.data.groupedRows,
      agents: this.agentsList,
      riskLevels: this.riskLevels,
      riskAssessment: this.riskAssessment,
      qualitativeExceedanceLabel: this.qualitativeExceedanceLabel,
      qualitativeFrequencyLabel: this.qualitativeFrequencyLabel,
      exposureLikelihoodLevels: this.exposureLikelihoodLevels,
      selectedSegIds: selectedSegs.map(s => s.segId),
      selectedAgentIds: selectedAgents.map(a => a.agentId),
      selectedStatisticKeys: selectedStats.map(s => s.key)
    };
    this.showReportGeneration = true;
    this.isGeneratingReport = false;
  }

  async exportToExcel() {
    const selectedSegs = this.segsList.filter(s => s.selected);
    const selectedAgents = this.agentsList.filter(a => a.selected);
    const selectedStats = this.statisticList.filter(s => s.selected);

    if (!selectedSegs.length || !selectedAgents.length) {
      this.toastService.showInfo(this.translateService.getDynamicTranslation('Nothing to export'), false);
      return;
    }

    const [{ Workbook }] = await Promise.all([
      import(/* webpackChunkName: "exceljs" */ 'exceljs')
    ]);
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet(this.translateService.getDynamicTranslation('Risk Register'));

    const header = [
      this.translateService.getDynamicTranslation('SEG Code'),
      this.translateService.getDynamicTranslation('SEG Name'),
      this.translateService.getDynamicTranslation('No of Employees'),
      this.translateService.getDynamicTranslation('No of Contractors'),
      ...selectedAgents.map(a => a.agentName)
    ];

    sheet.addRow(header);

    sheet.getRow(1).eachCell(cell => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFEAF0FF' }
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
    });

    const selectedSegIds = new Set(selectedSegs.map(seg => seg.segId));
    const exportRows: PreviewRow[] = this.data.groupedRows
      ? this.buildGroupedRows(selectedSegIds)
      : selectedSegs.map(seg => this.dataList.find(x => x.segId === seg.segId)).filter((row): row is RiskRegister => !!row);

    exportRows.forEach(segData => {
      if (this.isGroupHeader(segData)) {
        const groupRow = sheet.addRow([segData.label]);
        groupRow.font = { bold: true };
        sheet.mergeCells(groupRow.number, 1, groupRow.number, header.length);
        return;
      }

      const rowValues: (string | { text: string; color: string } | number)[] = [
        segData.segCode ?? '-',
        segData.segName,
        segData.numberOfWorkers ?? '',
        segData.numberOfContractors ?? ''
      ];

      selectedAgents.forEach(agent => {
        const reg = (segData.registers as RiskRegisterAgentEntry[]).find(r => r.agentId === agent.agentId);

        if (!reg) {
          rowValues.push('');
          return;
        }

        const statLines = selectedStats.map(stat =>
          `${stat.value} = ${getRegisterStatisticValue(reg, stat.key, this.exposureLikelihoodLevels, this.notAvailable)}`);

        const formatted = statLines.join(', \n');
        rowValues.push({ text: formatted, color: reg.color ?? '' });
      });

      const row = sheet.addRow(rowValues);

      row.eachCell((cell, colIndex) => {
        const cellValue = rowValues[colIndex - 1];

        cell.alignment = { wrapText: true, vertical: 'top' };
        cell.border = {
          top: { style: 'thin' }, left: { style: 'thin' },
          bottom: { style: 'thin' }, right: { style: 'thin' }
        };

        if (colIndex <= this.fixedColumns.length) return;
        if (cellValue && typeof cellValue === 'object' && (cellValue as { color?: string }).color) {
          const argb = this.toARGB((cellValue as { color: string }).color);
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
        } else {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };
        }

        if (cellValue && typeof cellValue === 'object') {
          cell.value = (cellValue as { text: string }).text;
        }
      });
    });

    sheet.columns.forEach(col => {
      col.width = 30;
    });

    workbook.xlsx.writeBuffer().then(buffer => {
      this.downloadBlob(new Blob([buffer]), 'Risk_Register_Report.xlsx');
    });
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    downloadBlob(blob, fileName);
  }

  private toARGB(color: string): string {
    if (!color) return 'FFD9D9D9';

    let r = 217, g = 217, b = 217;

    if (color.startsWith('#')) {
      let hex = color.replace('#', '');

      if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
      if (hex.length === 8) hex = hex.substring(0, 6);

      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (color.startsWith('rgb')) {
      const nums = color.replace(/[^\d.,]/g, '').split(',').map(n => parseFloat(n.trim()));
      [r, g, b] = nums;
    }

    const isGrey = Math.abs(r - g) < 10 && Math.abs(g - b) < 10;
    if (isGrey) {
      if (r < 200) return 'FFD9D9D9';
    }

    return ('FF' + [r, g, b]
      .map(v => v.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
    );
  }

  private buildRiskLevelLegendName(level: RiskLevel, index: number): string {
    return level?.Name?.trim() || `${this.translateService.getDynamicTranslation('Rating')} ${index}`;
  }

  public trackByIndex = trackByIndex;
}
